<template>
  <el-dialog
    title="消息搜索"
    :visible.sync="visible"
    width="620px"
    :close-on-click-modal="false"
    append-to-body
    class="search-dialog"
  >
    <div class="search-header">
      <el-input
        v-model.trim="keyword"
        placeholder="输入关键词搜索历史消息"
        clearable
        prefix-icon="el-icon-search"
        @keyup.enter.native="onSearch"
      />
      <el-button type="primary" :disabled="!keyword" @click="onSearch">搜索</el-button>
    </div>
    <div class="search-tip">
      <span>仅从本地缓存的聊天记录中检索，不会发起网络请求。</span>
    </div>
    <div class="result-wrapper" v-loading="searching">
      <div v-if="!results.length && searched" class="empty-tip">
        未搜索到"{{ keyword }}"相关的消息
      </div>
      <div v-else-if="!searched" class="empty-tip">输入关键词开始检索历史消息</div>
      <div v-else>
        <div
          class="result-item"
          v-for="(item, index) in results"
          :key="`${item.chatType}-${item.targetId}-${item.messageId || index}`"
          @click="onSelectResult(item)"
        >
          <div class="result-meta">
            <div class="chat-name">
              <i class="el-icon-chat-line-square"></i>
              <span>{{ item.chatName || '未知会话' }}</span>
            </div>
            <div class="message-time">{{ formatTime(item.sendTime) }}</div>
          </div>
          <div class="result-content">
            <span class="sender" v-if="item.sender">{{ item.sender }}：</span>
            <span class="message">{{ item.displayContent }}</span>
          </div>
          <div class="result-tags">
            <el-tag size="mini" effect="plain">{{ chatTypeLabel(item.chatType) }}</el-tag>
            <el-tag v-if="item.chatCompany" size="mini" effect="plain">{{ item.chatCompany }}</el-tag>
          </div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'SearchDialog',
  data() {
    return {
      visible: false,
      keyword: '',
      results: [],
      searching: false,
      searched: false
    };
  },
  methods: {
    open() {
      this.visible = true;
    },
    close() {
      this.visible = false;
    },
    onSearch() {
      if (!this.keyword) {
        this.$message.warning('请输入关键词');
        return;
      }
      this.searching = true;
      const keyword = this.keyword.toLowerCase();
      const chats = typeof this.chatStore.findChats === 'function' ? this.chatStore.findChats() : [];
      const matches = [];
      chats.forEach(chat => {
        if (!chat || !Array.isArray(chat.messages)) {
          return;
        }
        chat.messages.forEach(msg => {
          const text = this.extractMessageText(msg);
          if (!text) {
            return;
          }
          if (text.toLowerCase().includes(keyword)) {
            matches.push(this.buildResult(chat, msg, text));
          }
        });
      });
      matches.sort((a, b) => (b.sendTime || 0) - (a.sendTime || 0));
      this.results = matches.slice(0, 300);
      this.searched = true;
      this.searching = false;
    },
    buildResult(chat, msg, text) {
      return {
        chatType: chat.type,
        targetId: chat.targetId,
        chatName: chat.showName,
        chatCompany: chat.companyName,
        sendTime: msg.sendTime,
        sender: this.resolveSender(chat, msg),
        displayContent: text,
        messageId: msg.id
      };
    },
    extractMessageText(msg) {
      if (!msg || !msg.content) {
        return '';
      }
      const messageType = this.$enums.MESSAGE_TYPE;
      const type = msg.type;
      if (
        type === messageType.TEXT ||
        type === messageType.TIP_TEXT ||
        type === messageType.SYSTEM_MESSAGE
      ) {
        return this.stripHtml(msg.content);
      }
      if (type === messageType.FILE) {
        const data = this.safeJsonParse(msg.content);
        return data && data.name ? `[文件] ${data.name}` : '';
      }
      if (type === messageType.USER_CARD) {
        const data = this.safeJsonParse(msg.content);
        return data && data.nickName ? `[个人名片] ${data.nickName}` : '';
      }
      if (type === messageType.GROUP_CARD) {
        const data = this.safeJsonParse(msg.content);
        return data && data.groupName ? `[群名片] ${data.groupName}` : '';
      }
      if (type === messageType.STICKER) {
        const data = this.safeJsonParse(msg.content);
        return data && data.name ? `[表情] ${data.name}` : '';
      }
      return '';
    },
    resolveSender(chat, msg) {
      if (chat.type === 'GROUP') {
        if (msg.selfSend) {
          return this.userStore.userInfo.nickName;
        }
        return msg.sendNickName || '';
      }
      if (msg.selfSend) {
        return '我';
      }
      return chat.showName;
    },
    formatTime(time) {
      if (!time) {
        return '';
      }
      const text = this.$date.formatDateTime(time);
      return text || '';
    },
    chatTypeLabel(type) {
      if (type === 'GROUP') {
        return '群聊';
      }
      if (type === 'PRIVATE') {
        return '单聊';
      }
      return '系统';
    },
    onSelectResult(item) {
      if (!item) {
        return;
      }
      const chatInfo = { type: item.chatType, targetId: item.targetId };
      this.chatStore.openChat(chatInfo);
      this.chatStore.setActiveChat(chatInfo);
      this.$router.push('/home/chat').catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err;
        }
      });
      this.close();
    },
    stripHtml(text) {
      return (text || '').replace(/<[^>]+>/g, '');
    },
    safeJsonParse(content) {
      try {
        return JSON.parse(content);
      } catch (e) {
        return null;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.search-dialog {
  :deep(.el-dialog__body) {
    padding: 0 20px 20px;
    max-height: 60vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .search-header {
    display: flex;
    gap: 12px;
    align-items: center;
    margin-bottom: 8px;
    flex: 0 0 auto;

    :deep(.el-input) {
      flex: 1;
    }

    :deep(.el-button) {
      height: 40px;
      padding: 0 24px;
    }
  }

  .search-tip {
    color: var(--im-text-color-light);
    font-size: var(--im-font-size-small);
    margin-bottom: 12px;
    flex: 0 0 auto;
  }

  .result-wrapper {
    flex: 1;
    overflow-y: auto;
    min-height: 200px;
  }

  .empty-tip {
    text-align: center;
    color: var(--im-text-color-light);
    padding: 40px 0;
  }

  .result-item {
    padding: 12px 16px;
    cursor: pointer;
    border-bottom: 1px solid #f5f5f5;
    transition: background 0.2s ease;
    text-align: left;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: #f9fbff;
    }

    .result-meta {
      display: flex;
      justify-content: space-between;
      color: var(--im-text-color-light);
      font-size: var(--im-font-size-small);
      margin-bottom: 4px;

      .chat-name {
        display: flex;
        align-items: center;
        gap: 6px;
        color: var(--im-color-primary);
      }
    }

    .result-content {
      font-size: var(--im-font-size);
      color: var(--im-text-color);
      line-height: 1.6;

      .sender {
        font-weight: 600;
      }

      .message {
        word-break: break-all;
      }
    }

    .result-tags {
      margin-top: 8px;

      .el-tag {
        margin-right: 6px;
      }
    }
  }
}
</style>
